import type { AuthChangeEvent, Session } from '@supabase/supabase-js';

import { supabase } from '@/lib/supabaseClient';
import type { PlatformIdentity } from '@/platform/types';
import {
  PlatformSessionError,
  requestPlatformSession,
  type PlatformSessionSignals,
} from '@/services/platformSession';

/**
 * Анонимная сессия по умолчанию.
 *
 * Продукт не требует регистрации: анонимный вход даёт userId, на который
 * заводятся кредиты и генерации при первом обращении к серверу (см.
 * onboard_user в generate/index.ts). Остаётся ровно для веба без входа —
 * «открыл и сделал портрет» часть продукта, а не недоделка (ADR-041).
 * Промис мемоизируется на уровне модуля — React 19 StrictMode в dev
 * вызывает эффекты дважды, и без этого два параллельных вызова
 * signInAnonymously() создали бы двух разных пользователей за один рендер.
 */
let pendingSignIn: Promise<Session | null> | null = null;

export async function ensureAnonymousSession(): Promise<Session | null> {
  if (!supabase) {
    return null;
  }

  const { data } = await supabase.auth.getSession();
  if (data.session) {
    return data.session;
  }

  pendingSignIn ??= supabase.auth.signInAnonymously().then(({ data: signInData, error }) => {
    pendingSignIn = null;
    if (error) {
      throw error;
    }
    return signInData.session;
  });

  return pendingSignIn;
}

/**
 * Сессия платформенного пользователя (ADR-041).
 *
 * Владельца определяет подпись площадки, а не хранилище webview: сервер
 * находит или заводит нужный user_id и, если текущая сессия не его,
 * выдаёт одноразовый token_hash. Мемоизация промиса здесь не украшение,
 * а обязательное условие: Supabase внутри окна выдачи ссылок возвращает
 * на повторный запрос тот же — уже потраченный — токен (gotrue-js#784),
 * поэтому второй вызов эффекта из StrictMode обязан получить тот же
 * промис, а не сминтить второй токен.
 */
let pendingPlatformSignIn: Promise<Session | null> | null = null;

export async function ensurePlatformSession(
  identity: Exclude<PlatformIdentity, null>,
  signals: PlatformSessionSignals,
): Promise<Session | null> {
  const client = supabase;

  if (!client) {
    return null;
  }

  pendingPlatformSignIn ??= startPlatformSession(client, identity, signals).finally(() => {
    pendingPlatformSignIn = null;
  });

  return pendingPlatformSignIn;
}

async function startPlatformSession(
  client: NonNullable<typeof supabase>,
  identity: Exclude<PlatformIdentity, null>,
  signals: PlatformSessionSignals,
): Promise<Session | null> {
  // Текущая сессия отправляется как есть: решает сервер, та ли это
  // сессия. Клиент не вправе считать «у меня есть токен — значит вход
  // не нужен»: в хранилище может лежать чужой или анонимный.
  const { data } = await client.auth.getSession();
  const result = await requestPlatformSession(
    identity,
    signals,
    data.session?.access_token ?? undefined,
  );

  if (result.status === 'current') {
    return data.session;
  }

  const { data: verified, error } = await client.auth.verifyOtp({
    token_hash: result.tokenHash,
    type: 'magiclink',
  });

  if (error) {
    /*
     * Наружу идёт русский текст «что случилось + что делать»: сообщение
     * Supabase английское и техническое, показывать его человеку нельзя.
     */
    throw new PlatformSessionError('Не удалось войти. Откройте приложение снова.', 'AUTH_ERROR');
  }

  return verified.session;
}

/**
 * Забыть текущую сессию.
 *
 * Два вызывающих: удаление аккаунта (пользователя не стало, а его JWT в
 * localStorage ещё не истёк и выглядел бы рабочим) и привязка площадок
 * (сессия могла принадлежать поглощённому аккаунту). Выход только на этом
 * устройстве: остальные устройства выжившего аккаунта разлогинивать нельзя. После
 * вызова AuthProvider на следующем рендере заведёт сессию тем же путём,
 * что при самом первом открытии приложения: анонимную в вебе, новую
 * платформенную в Mini App.
 */
export async function forgetSession(): Promise<void> {
  await supabase?.auth.signOut({ scope: 'local' });
}

export function subscribeToAuthChanges(
  callback: (event: AuthChangeEvent, session: Session | null) => void,
): () => void {
  if (!supabase) {
    return () => {};
  }

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange(callback);

  return () => subscription.unsubscribe();
}
